import { useEffect, useRef, useState, useMemo } from 'react';
import { isSeatedInfant } from '@/utils/passengerTypes';

/**
 * Fetches minimum flight prices per departure date from
 * `/api/flight-calendar-prices` and returns them in a map keyed by
 * `YYYY-MM-DD` so the calendar can show a "$X" hint under each date.
 *
 * Debounced against rapid input changes (origin/destination typing,
 * month navigation) — a single 400ms trailing edge before we spend a
 * SearchAPI call. Also short-circuits when either airport is empty
 * so we don't fire until the customer has both endpoints selected.
 *
 * `flightType`:
 *   - `'one_way'` (default) — prices reflect a single outbound leg.
 *   - `'round_trip'` — prices are the SUM of one-way outbound plus
 *     one-way return shifted by `tripLengthDays`. Matches what the
 *     customer sees when they actually search a round-trip, so the
 *     calendar hint is no longer misleadingly-cheap-by-half.
 *
 * @param {Object} opts
 * @param {string} opts.origin           IATA code (3 letters). Skips fetch if falsy.
 * @param {string} opts.destination      IATA code (3 letters). Skips fetch if falsy.
 * @param {Date}   opts.startDate        First date the calendar is showing (UTC-safe).
 * @param {number} [opts.days=62]        Days of coverage from `startDate`. Cap 62 per SearchAPI.
 * @param {string} [opts.cabinClass]     'economy' / 'premium_economy' / 'business' / 'first'
 * @param {Array}  [opts.passengers]     Passenger array as used elsewhere in the app.
 * @param {boolean}[opts.enabled=true]   Master switch; wire to date-picker open state.
 * @param {string} [opts.flightType='one_way']  'one_way' or 'round_trip'.
 * @param {number} [opts.tripLengthDays=7]  Days between outbound and return legs
 *                                          when `flightType='round_trip'`.
 * @returns {{ prices: Object<string, number>, lowestPrice: number|null, currency: string, isLoading: boolean }}
 */
export function useFlightCalendarPrices({
  origin,
  destination,
  startDate,
  days = 62,
  cabinClass = 'economy',
  passengers,
  enabled = true,
  flightType = 'one_way',
  tripLengthDays = 7,
}) {
  const [state, setState] = useState({
    prices: {},
    lowestPrice: null,
    currency: 'USD',
    isLoading: false,
  });

  // Stable primitive dep-keys so the effect doesn't refire on object-
  // identity changes when the caller reconstructs `passengers` every
  // render.
  const startYMD = useMemo(() => {
    if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
    const y = startDate.getUTCFullYear();
    const m = String(startDate.getUTCMonth() + 1).padStart(2, '0');
    const d = String(startDate.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [startDate]);
  const passengerFingerprint = useMemo(() => {
    if (!Array.isArray(passengers)) return 'a1';
    const counts = { adult: 0, child: 0, infant_in_seat: 0, infant_without_seat: 0 };
    for (const p of passengers) {
      if (isSeatedInfant(p?.type)) counts.infant_in_seat++;
      else if (p?.type && counts[p.type] != null) counts[p.type]++;
      else counts.adult++;
    }
    return `a${counts.adult}c${counts.child}is${counts.infant_in_seat}il${counts.infant_without_seat}`;
  }, [passengers]);

  // The AbortController lets us cancel an in-flight fetch when inputs
  // change again mid-request — without this, an old fetch could
  // resolve after a newer one and clobber fresher data.
  const inFlightRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const originIata = String(origin || '').trim().toUpperCase();
    const destIata = String(destination || '').trim().toUpperCase();
    if (!originIata || !destIata || originIata === destIata) return;
    if (!startYMD) return;

    const controller = new AbortController();
    if (inFlightRef.current) inFlightRef.current.abort();
    inFlightRef.current = controller;

    const debounce = setTimeout(async () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetch('/api/flight-calendar-prices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            origin: originIata,
            destination: destIata,
            start_date: startYMD,
            days,
            cabin_class: cabinClass || 'economy',
            passengers: Array.isArray(passengers) ? passengers : undefined,
            currency: 'USD',
            flight_type: flightType === 'round_trip' ? 'round_trip' : 'one_way',
            trip_length_days: flightType === 'round_trip' ? tripLengthDays : undefined,
          }),
        });
        if (!response.ok) throw new Error(`Calendar prices ${response.status}`);
        const data = await response.json();
        if (controller.signal.aborted) return;
        setState({
          prices: data?.prices || {},
          lowestPrice: data?.lowestPrice ?? null,
          currency: data?.currency || 'USD',
          isLoading: false,
        });
      } catch (err) {
        if (controller.signal.aborted || err?.name === 'AbortError') return;
        // Silent degrade — calendar keeps working without prices.
        setState({ prices: {}, lowestPrice: null, currency: 'USD', isLoading: false });
      }
    }, 400);

    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
    // startYMD collapses the Date reference to a stable string, and
    // passengerFingerprint does the same for the passengers array —
    // both are the actual dependency signals.
  }, [enabled, origin, destination, startYMD, days, cabinClass, passengerFingerprint, passengers, flightType, tripLengthDays]);

  return state;
}
